import { useEffect, useRef, useState } from 'react';
import { Loader2, Variable } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useAvailableVariables,
  useCreateBlock,
  useUpdateBlock,
} from '../hooks/use-dossier-company';
import { BLOCK_CATEGORIES_ORDER, BLOCK_CATEGORY_LABELS } from './piece-types';
import type {
  BlockCategory,
  BlockStatus,
  KnowledgeBlock,
} from '../types/dossier-company.types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: KnowledgeBlock | null;
}

const BLOCK_STATUSES: { value: BlockStatus; label: string }[] = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'actif', label: 'Actif' },
  { value: 'archive', label: 'Archivé' },
];

export function BlockEditorDialog({ open, onOpenChange, block }: Props) {
  const create = useCreateBlock();
  const update = useUpdateBlock();
  const { data: variables } = useAvailableVariables();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<BlockCategory>('presentation_societe');
  const [tags, setTags] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<BlockStatus>('brouillon');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (block) {
      setTitle(block.title);
      setCategory(block.category);
      setTags(block.tags.join(', '));
      setContent(block.content);
      setStatus(block.status);
    } else {
      setTitle('');
      setCategory('presentation_societe');
      setTags('');
      setContent('');
      setStatus('brouillon');
    }
  }, [block, open]);

  function insertVariable(placeholder: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = content.substring(0, start) + placeholder + content.substring(end);
    setContent(next);
    // Restaure le curseur après le placeholder
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  }

  async function handleSubmit() {
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const payload = {
      title,
      category,
      tags: parsedTags,
      content,
      status,
    };

    if (block) {
      await update.mutateAsync({ blockId: block.id, input: payload });
    } else {
      await create.mutateAsync(payload);
    }
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;
  const groupedVars = variables
    ? variables.reduce<Record<string, typeof variables>>((acc, v) => {
        if (!acc[v.namespace]) acc[v.namespace] = [];
        acc[v.namespace]!.push(v);
        return acc;
      }, {})
    : {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{block ? 'Modifier le bloc' : 'Nouveau bloc rédactionnel'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="title">Titre</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex. Notre démarche sécurité"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Catégorie</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as BlockCategory)}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BLOCK_CATEGORIES_ORDER.map((c) => (
                    <SelectItem key={c} value={c}>
                      {BLOCK_CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Statut</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as BlockStatus)}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BLOCK_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="tags">Tags (séparés par virgule)</Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="ex. secteur public, santé, industrie"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="content">Contenu</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Variable className="mr-2 h-4 w-4" />
                    Insérer variable
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="max-h-[400px] overflow-y-auto" align="end">
                  {(['acheteur', 'marche', 'nous'] as const).map((ns) => (
                    <div key={ns}>
                      <DropdownMenuLabel className="text-xs uppercase tracking-wider">
                        {ns === 'acheteur' ? 'Acheteur' : ns === 'marche' ? 'Marché' : 'Nous'}
                      </DropdownMenuLabel>
                      {groupedVars[ns]?.map((v) => (
                        <DropdownMenuItem
                          key={v.placeholder}
                          onClick={() => insertVariable(v.placeholder)}
                        >
                          <div className="flex flex-col">
                            <code className="text-xs">{v.placeholder}</code>
                            <span className="text-xs text-muted-foreground">{v.label}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Textarea
              id="content"
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={14}
              className="font-mono text-sm"
              placeholder="Texte du bloc. Utilisez {{nous.denomination}}, {{marche.titre}}, etc."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!title || !content || pending}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {block ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
